// Inline-image resolver for Microsoft Graph emails.
//
// Email HTML often references inline images as `cid:<content-id>`. Browsers
// can't render those directly — we have to fetch each attachment via the
// Graph download endpoint and swap the src for a data: URL. This util is
// shared between EmailView (side panel), TerminalEmailView (terminal mail
// view), and TerminalWorkspaceStream (job-workspace thread pane) so the
// resolution logic stays consistent and the attachment cache is shared.
//
// IMPORTANT: Even when fetch fails, the cid: src is always stripped first —
// otherwise the <img> triggers ERR_UNKNOWN_URL_SCHEME in the browser
// console for every unresolved image.

import { authFetch } from '../../../utils/cloudFunctionsAuth';

export interface InlineAttachment {
  id?: string;
  filename: string;
  contentType?: string;
  size?: number | null;
  isInline?: boolean;
  contentId?: string | null;
  /** Graph @odata.type — distinguishes attached EMAILS (itemAttachment,
   *  message/rfc822) from regular file attachments; they need a different
   *  open path (no contentBytes to download). */
  odataType?: string | null;
}

// ── Cid helpers ──────────────────────────────────────────────────────────

export const normalizeCid = (value: string): string => {
  return value
    .replace(/^cid:/i, '')
    .replace(/^<|>$/g, '')
    .trim()
    .toLowerCase();
};

export const cidCandidates = (value: string): string[] => {
  const n = normalizeCid(value);
  if (!n) return [];
  const out = new Set<string>();
  out.add(n);
  const at = n.indexOf('@');
  if (at > 0) out.add(n.slice(0, at));
  return Array.from(out);
};

/**
 * Strip all cid: URLs from an HTML string. Used as a fallback when we can't
 * fetch the inline attachments (offline, auth failure, etc.) — keeps the
 * browser console clean even if the images don't render.
 */
export const stripCidImages = (html: string): string => {
  if (!html || !/cid:/i.test(html)) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    Array.from(doc.querySelectorAll('img[src^="cid:"]')).forEach((img) => {
      img.removeAttribute('src');
      img.setAttribute('data-wc-missing-cid', 'true');
    });
    Array.from(doc.querySelectorAll('[href^="cid:"], [xlink\\:href^="cid:"]')).forEach((el) => {
      el.removeAttribute('href');
      el.removeAttribute('xlink:href');
      el.setAttribute('data-wc-missing-cid', 'true');
    });
    return doc.body?.innerHTML || html;
  } catch {
    return html
      .replace(/(\ssrc=["'])cid:[^"']*(["'])/gi, '$1$2')
      .replace(/(\shref=["'])cid:[^"']*(["'])/gi, '$1$2')
      .replace(/(\sxlink:href=["'])cid:[^"']*(["'])/gi, '$1$2')
      .replace(/url\(\s*cid:[^)]+\)/gi, 'url()');
  }
};

// ── Network helpers ──────────────────────────────────────────────────────

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read attachment'));
    reader.readAsDataURL(blob);
  });
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const isRetryableNetworkError = (e: any): boolean => {
  const msg = String(e?.message || e || '').toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed') ||
    msg.includes('err_failed') ||
    msg.includes('503') ||
    msg.includes('service unavailable') ||
    msg.includes('cors')
  );
};

async function withRetries<T>(fn: () => Promise<T>, opts?: { retries?: number; baseDelayMs?: number }): Promise<T> {
  const retries = opts?.retries ?? 3;
  const baseDelayMs = opts?.baseDelayMs ?? 350;
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (e) {
      attempt++;
      if (attempt > retries || !isRetryableNetworkError(e)) throw e;
      const jitter = Math.floor(Math.random() * 200);
      const delay = baseDelayMs * Math.pow(2, attempt - 1) + jitter;
      await sleep(delay);
    }
  }
}

// ── Module-level caches (shared across views) ────────────────────────────

// Attachment-list cache, keyed by emailId. Survives across panes / surfaces
// so opening the same email in the workspace thread pane after the side
// panel doesn't re-fetch the list.
const attachmentListCache = new Map<string, InlineAttachment[]>();
// Inline-attachment data-URL cache, keyed by attachment id. Same rationale.
const inlineDataUrlCache = new Map<string, string>();

export const fetchGraphAttachmentsList = async (emailId: string): Promise<InlineAttachment[]> => {
  const cached = attachmentListCache.get(emailId);
  if (cached) return cached;
  const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/getGraphEmailAttachments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emailId }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || data?.ok !== true) {
    throw new Error(data?.error || `Failed to load attachments (HTTP ${resp.status})`);
  }
  const list = Array.isArray(data?.attachments) ? data.attachments : [];
  const mapped: InlineAttachment[] = list.map((a: any) => ({
    id: a.id,
    filename: a.name || 'attachment',
    contentType: a.contentType || 'application/octet-stream',
    size: typeof a.size === 'number' ? a.size : null,
    isInline: !!a.isInline,
    contentId: a.contentId || null,
    odataType: a['@odata.type'] || null,
  }));
  attachmentListCache.set(emailId, mapped);
  return mapped;
};

const fetchInlineAttachmentDataUrl = async (emailId: string, att: InlineAttachment): Promise<string> => {
  if (!att.id) throw new Error('Attachment id missing');
  const cached = inlineDataUrlCache.get(att.id);
  if (cached) return cached;
  const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/downloadGraphAttachment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emailId, attachmentId: att.id }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data?.error || `Download failed (HTTP ${resp.status})`);
  }
  const blob = await resp.blob();
  const dataUrl = await blobToDataUrl(blob);
  inlineDataUrlCache.set(att.id, dataUrl);
  return dataUrl;
};

// ── Main entry point ─────────────────────────────────────────────────────

export interface ResolveOptions {
  /** Optional pre-fetched attachment list — saves a round-trip when the
   *  caller has already loaded it (e.g. EmailView's attachments panel). */
  attachments?: InlineAttachment[] | null;
  onProgress?: (p: { total: number; done: number; failed: number }) => void;
}

/**
 * Resolve `cid:` images in `html` to data: URLs by fetching the matching
 * Graph attachments. Cid URLs are stripped synchronously up-front so the
 * returned HTML never produces ERR_UNKNOWN_URL_SCHEME — even if every
 * downstream fetch fails the markup is safe to inject.
 */
export const resolveInlineGraphImages = async (
  emailId: string,
  html: string,
  opts?: ResolveOptions,
): Promise<string> => {
  if (!emailId || !html || !/cid:/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const images = Array.from(doc.querySelectorAll('img[src^="cid:"]'));
  if (images.length === 0) return html;

  // Strip cid: before any async work so partial failures are still safe.
  // Capture the original cid + alt before stripping so we have both signals
  // for the filename / alt-text fallback matching strategies below.
  const imgInfo = images.map((img) => {
    const rawSrc = img.getAttribute('src') || '';
    const cid = normalizeCid(rawSrc);
    const alt = (img.getAttribute('alt') || '').trim();
    img.removeAttribute('src');
    img.setAttribute('data-wc-cid', cid || 'true');
    if (alt) img.setAttribute('data-wc-alt', alt);
    return { img, cid, alt };
  });

  let list: InlineAttachment[] = [];
  try {
    list = (opts?.attachments && opts.attachments.length > 0)
      ? opts.attachments
      : await fetchGraphAttachmentsList(emailId);
  } catch (fetchErr) {
    console.warn('[resolveInlineGraphImages] Failed to fetch attachments list:', fetchErr);
    return doc.body?.innerHTML || html;
  }

  // Build lookup tables for matching:
  //   1. by contentId (the standard path)
  //   2. by filename — some senders (Apple Mail in particular) strip
  //      contentId in transit, so we fall back to filename either via the
  //      img's `alt` attribute or via the cid value itself.
  const byCid = new Map<string, InlineAttachment>();
  const byFilename = new Map<string, InlineAttachment>();
  list.forEach((att) => {
    if (att.contentId) {
      cidCandidates(att.contentId).forEach((k) => byCid.set(k, att));
    }
    if (att.filename) byFilename.set(att.filename.toLowerCase(), att);
  });

  const findAttForImage = (info: { cid: string; alt: string }): InlineAttachment | undefined => {
    const direct = cidCandidates(info.cid).map((c) => byCid.get(c)).find(Boolean);
    if (direct) return direct;
    if (info.alt) {
      const byAlt = byFilename.get(info.alt.toLowerCase());
      if (byAlt) return byAlt;
    }
    const byCidName = byFilename.get(info.cid.toLowerCase());
    if (byCidName) return byCidName;
    return undefined;
  };

  const needed = new Map<string, InlineAttachment>();
  imgInfo.forEach(({ cid, alt }) => {
    const att = findAttForImage({ cid, alt });
    if (att?.id) needed.set(att.id, att);
  });

  if (needed.size === 0) {
    return doc.body?.innerHTML || html;
  }

  const queue = Array.from(needed.values());
  const total = queue.length;
  let done = 0;
  let failed = 0;
  opts?.onProgress?.({ total, done, failed });
  const CONCURRENCY = 2;
  let idx = 0;
  const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }).map(async () => {
    while (idx < queue.length) {
      const current = queue[idx++];
      try {
        await withRetries(() => fetchInlineAttachmentDataUrl(emailId, current), { retries: 3, baseDelayMs: 400 });
        done += 1;
      } catch {
        failed += 1;
      }
      opts?.onProgress?.({ total, done, failed });
    }
  });
  await Promise.all(workers);

  imgInfo.forEach(({ img, cid, alt }) => {
    const att = findAttForImage({ cid, alt });
    if (!att?.id) return;
    const dataUrl = inlineDataUrlCache.get(att.id);
    if (dataUrl) img.setAttribute('src', dataUrl);
  });

  return doc.body?.innerHTML || html;
};
